import React from "react";
import moment from "moment";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import Dropdown from "components/common/Dropdown";
import CustomTable from "components/common/CustomTable";
import { Space, SpaceTaskFilters, TaskScheduleTypeEnum } from "redux/Ldb/types/spaceTypes";
import { normalizeMembers } from "utils/spaceUtils";
import { UseGetSpaceTasksReturn } from "hooks/spaceHooks/useGetSpaceTasks";
import EmptyStateComp from "components/common/EmptyStateComp";
import { capitalizeFirstLetter } from "helpers/transformStrings";

type SpaceTaskRecurringListProps = {
    spaceData: Space;
    getSpaceTaskListQuery: UseGetSpaceTasksReturn;
    filters: SpaceTaskFilters;
    setFilters: (filters: SpaceTaskFilters) => void;
    setTaskType: (type: string) => void;
    setTaskId: (taskId: string) => void;
};

const SpaceTaskRecurringList = (props: SpaceTaskRecurringListProps) => {
    const { spaceData, getSpaceTaskListQuery, filters, setFilters, setTaskType, setTaskId } = props;
    const { spaceId } = useParams();
    const navigate = useNavigate();
    const members = normalizeMembers(spaceData);
    const [searchParams] = useSearchParams();
    const tabId = searchParams.get("tab");
    const tableHeader = [
        { title: "Task name", widthClass: "w-[383px]" },
        { title: "Runs Completed", widthClass: "w-[130px]" },
        { title: "Created by", widthClass: "w-[198px]" },
        { title: "Assigned To", widthClass: "w-[198px]" },
        { title: "Frequency", widthClass: "w-[120px]" },
        { title: "Last run date", widthClass: "w-[120px]" },
        { title: "Last run date", widthClass: "w-[148px]" },
        { title: "Status", widthClass: "w-[148px]" },
        { title: "", widthClass: "w-[48px]" },
    ];

    const tableBody = getSpaceTaskListQuery?.data?.tasks?.map((task, idx) => {
        const assigneeDetails =
            task?.assignees[0]?.kind !== "synth" &&
            members?.find(
                (member) =>
                    member?.ldbId === task?.assignees[0]?.ldbId ||
                    member?.guestId === task?.assignees[0]?.guestId
            );
        return [
            {
                content: (
                    <div
                        key={idx}
                        onClick={() => {
                            setFilters({
                                ...filters,
                                taskType: TaskScheduleTypeEnum.recurring,
                            });
                        }}
                        className={`py-3 pr-3 flex font-rocGroteskMedium items-center h-full border-slate-100 text-sm text-slate-500`}
                    >
                        <p className="">{task?.name}</p>
                    </div>
                ),
                cellClickAction: () => {
                    setTaskType("recurring");
                    setTaskId(task?._id);
                    navigate(`/spaces/${spaceId}?tab=${tabId || 3}&taskId=${task?._id}`);
                },
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 `}
                    >
                        <p className="">{0}</p>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-500 `}
                    >
                        <div className="flex items-center space-x-2 ">
                            <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
                                <i className="ri-user-follow-line text-gm-50"></i>
                            </div>
                            <span className="break-all">
                                {task?.createdBy?.kind === "synth"
                                    ? "Synth"
                                    : task?.createdBy?.ldb?.fullName ||
                                      [
                                          task?.createdBy?.guest?.firstName,
                                          task?.createdBy?.guest?.lastName,
                                      ]
                                          .join(" ")
                                          .trim() ||
                                      task?.createdBy?.guest?.email}
                            </span>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-500 `}
                    >
                        <div className="flex items-center space-x-2 ">
                            <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
                                <i className="ri-user-follow-line text-gm-50"></i>
                            </div>
                            <span className="break-all">
                                {task?.assignees[0]?.kind === "synth"
                                    ? "Synth"
                                    : assigneeDetails?.fullName ||
                                      assigneeDetails?.email ||
                                      assigneeDetails?.businessName}
                            </span>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-500 `}
                    >
                        <p className="">
                            {capitalizeFirstLetter(
                                task?.schedule?.recurrence?.frequency?.split("_")?.join(" ")
                            )}
                        </p>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-500 `}
                    >
                        <p className="">{moment(task?.schedule?.startAt).format("MMM DD, YYYY")}</p>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-500 `}
                    >
                        <p className="">
                            {moment(task?.schedule?.recurrence?.endAt).format("MMM DD, YYYY")}
                        </p>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-500 `}
                    >
                        <p className="flex items-center gap-2">
                            <div
                                className={`h-2 w-2 rounded-full bg-[#FF8A00] ${
                                    task?.status?.toLowerCase() === "completed" && "bg-g-55"
                                } ${task?.status?.toLowerCase() === "delayed" && "bg-[#FF8A00]"} ${
                                    task?.status?.toLowerCase() === "pending" && "bg-[#ffdd00]"
                                } ${task?.status?.toLowerCase() === "active" && "bg-g-25"} ${
                                    task?.status?.toLowerCase() === "failed" && "bg-r-50"
                                } ${task?.status?.toLowerCase() === "paused" && "bg-slate-100"}`}
                            ></div>
                            {capitalizeFirstLetter(task?.status)}
                        </p>
                    </div>
                ),
            },
            {
                content: (
                    <div className={`w-full  py-2`} onClick={(e) => e.stopPropagation()}>
                        <Dropdown
                            dropdown={<i className="ri-more-2-fill text-lg text-slate-500"></i>}
                            dropdownClassName={"!w-full !mb-10 !-mt-7"}
                            dropdownContainerClasses={`shadow-cardShadow-2 z-[1400] border-0 rounded !max-h-fit right-[0px] right-[40px]`}
                            name={"dropdown"}
                            dropdownItemsClasses="!bg-transparent "
                            dropdownOptions={[
                                {
                                    label: "View Task",
                                    action: () => {
                                        setTaskType("recurring");
                                        setTaskId(task?._id);
                                        navigate(
                                            `/spaces/${spaceId}?tab=${tabId || 3}&taskId=${
                                                task?._id
                                            }`
                                        );
                                    },
                                },
                            ]}
                        />
                    </div>
                ),
                widthClass: "!border-slate-100 !border-b",
            },
        ];
    });

    return (
        <CustomTable
            tableBody={tableBody}
            tableHeader={tableHeader}
            isScrollable
            scrollWithin
            isLoading={getSpaceTaskListQuery.isLoading || getSpaceTaskListQuery.isFetching}
            renderEmptyTable={() => (
                <EmptyStateComp
                    title="No Recurring tasks here yet"
                    description="You haven’t created any recurring tasks in this space. Use recurring tasks to track recurring activities with your team."
                    icon={<i className="ri-list-check-3"></i>}
                />
            )}
            isCellBordered
            isCellSelectable={false}
            headerContainerClass="!bg-slate-50 !border-slate-200"
            headerItemClass="font-rocGroteskMedium "
            tableClass="!border-slate-200"
        />
    );
};

export default SpaceTaskRecurringList;
